import * as React from 'react';
import propz from 'propz';
import { getSelectOptionForPositionsSport } from '../../helpers/table/select';
import { FunctionComponent } from 'react';
import { isClubEvent, isHousesEvent, isTeamOrTwoOnTwoSportEvent } from '../../helpers/event/event';
import { SchoolEvent } from '../../models/event';
import { Sport } from '../../models/sport';
import './SchoolEventPlayersSummary.scss';

interface Props {
  schoolEvent: SchoolEvent;
  sport: Sport;
  players: SchoolEventPlayerSummary[];
  teams: SchoolEventTeamSummary[];
  activeTeamIndex: number;
  isEditPositions: boolean;
  onChangeTeam: (event) => void;
  onChangePositionPlayer: (event, playerId: string) => void;
  onChangeIsSubPlayer: (event, playerId: string) => void;
  onChangeIsCaptainPlayer: (event, playerId: string) => void;
  onChangeIsViceCaptainPlayer?: (event, playerId: string) => void;
}

export interface SchoolEventPlayerSummary {
  name: string;
  form: string;
  playerId: string;
  userId?: string;
  permissionId?: string;
  avatar?: string;
  positionId?: string;
  isCaptain?: boolean;
  isViceCaptain?: boolean;
  isSub?: boolean;
  teamId?: string;
}

export interface SchoolEventTeamSummary {
  teamName: string;
  teamId: string;
  houseName?: string;
}

export const SchoolEventPlayersSummary: FunctionComponent<Props> = props => {
  const { isEditPositions, sport, schoolEvent, teams, players, activeTeamIndex } = props;
  const positions = getSelectOptionForPositionsSport(sport);
  const isTeamsExist = teams.length > 0;
  const isPositionsExist = positions.length > 0;
  const teamId = isTeamsExist ? teams[activeTeamIndex].teamId : undefined;

  const renderPositionsOptions = () => {
    return (
      <>
        <option value={''}>Not selected</option>;
        {positions.map(position => {
          return (
            <option key={position.value} value={position.value}>
              {position.text}
            </option>
          );
        })}
      </>
    );
  };

  const renderTeamOptions = () => {
    return teams.map((team, index) => {
      const teamName = isHousesEvent(schoolEvent) ? `${team.teamName} / ${team.houseName}` : team.teamName;
      const teamPlayersCount = players.filter(player => player.teamId === team.teamId).length;
      const studentsText = teamPlayersCount === 1 ? 'student' : 'students';
      return (
        <option key={index} value={team.teamId}>
          {teamName} · {teamPlayersCount} {studentsText}
        </option>
      );
    });
  };

  const renderSelectTeam = () => {
    return (
      <div className="eEventPositions__teamField">
        <label className="eEventPositions__label">Team</label>
        <div className="eEventPositions__hint">Choose the squad you want to review or edit.</div>
        <select className="eEventPositions__select" onChange={e => props.onChangeTeam(e)} value={teamId}>
          {renderTeamOptions()}
        </select>
      </div>
    );
  };

  const getPlayerInitials = (name: string) =>
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(namePart => namePart.charAt(0))
      .join('')
      .toUpperCase();

  const getPlayerAvatarUrl = (player: SchoolEventPlayerSummary) => {
    const imageSrc =
      propz.get(player, ['profile', 'avatar'], '') ||
      propz.get(player, ['user', 'profile', 'avatar'], '') ||
      propz.get(player, ['userProfile', 'avatar'], '') ||
      propz.get(player, ['profileData', 'avatar'], '') ||
      propz.get(player, ['avatar'], '') ||
      propz.get(player, ['avatarUrl'], '') ||
      propz.get(player, ['photoUrl'], '') ||
      propz.get(player, ['pic'], '') ||
      propz.get(player, ['photo'], '') ||
      propz.get(player, ['picture'], '') ||
      propz.get(player, ['image'], '') ||
      propz.get(player, ['imageUrl'], '');

    return imageSrc && String(imageSrc).startsWith('//') ? `https:${imageSrc}` : imageSrc;
  };

  const renderPlayers = () => {
    const textPlayers = isClubEvent(schoolEvent) ? 'No participants have been added yet.' : 'No participants';

    if (players.length > 0) {
      return players
        .filter(player => (isTeamsExist ? player.teamId === teamId : true))
        .map((player, index) => {
          const playerName = player.name;
          const playerForm = player.form;
          const avatarUrl = getPlayerAvatarUrl(player);

          return (
            <tr key={player.playerId}>
              <td className="eEventPositions__index">{index + 1}</td>
              <td>
                <div className="eEventPositions__player">
                  <span className="eEventPositions__avatar">
                    {avatarUrl ? (
                      <img className="eEventPositions__avatarImg" src={avatarUrl} alt="" />
                    ) : (
                      <span className="eEventPositions__avatarFallback">{getPlayerInitials(playerName)}</span>
                    )}
                  </span>
                  <div className="eEventPositions__playerName">{playerName}</div>
                </div>
              </td>
              {isPositionsExist && !isClubEvent(schoolEvent) && (
                <td>
                  <select
                    className="eEventPositions__select eEventPositions__select--table"
                    onChange={e => props.onChangePositionPlayer(e, player.playerId)}
                    value={player.positionId ? player.positionId : ''}
                    disabled={!isEditPositions}
                  >
                    {renderPositionsOptions()}
                  </select>
                </td>
              )}
              <td>
                <span className="eEventPositions__formPill">{playerForm || 'Not set'}</span>
              </td>

              {isTeamOrTwoOnTwoSportEvent(schoolEvent) && !isClubEvent(schoolEvent) && (
                <td className="eEventPositions__flagCell">
                  <label className="eEventPositions__check" aria-label={`Captain ${playerName}`}>
                    <input
                      type="checkbox"
                      checked={player.isCaptain}
                      onChange={e => props.onChangeIsCaptainPlayer(e, player.playerId)}
                      disabled={!isEditPositions}
                    />
                    <span>C</span>
                  </label>
                </td>
              )}
              {isTeamOrTwoOnTwoSportEvent(schoolEvent) && !isClubEvent(schoolEvent) && (
                <td className="eEventPositions__flagCell">
                  <label className="eEventPositions__check" aria-label={`Vice captain ${playerName}`}>
                    <input
                      type="checkbox"
                      checked={Boolean(player.isViceCaptain)}
                      onChange={e => props.onChangeIsViceCaptainPlayer?.(e, player.playerId)}
                      disabled={!isEditPositions || !props.onChangeIsViceCaptainPlayer}
                    />
                    <span>VC</span>
                  </label>
                </td>
              )}
              {isTeamOrTwoOnTwoSportEvent(schoolEvent) && !isClubEvent(schoolEvent) && (
                <td className="eEventPositions__flagCell">
                  <label
                    className="eEventPositions__check eEventPositions__check--sub"
                    aria-label={`Substitute ${playerName}`}
                  >
                    <input
                      type="checkbox"
                      checked={player.isSub}
                      onChange={e => props.onChangeIsSubPlayer(e, player.playerId)}
                      disabled={!isEditPositions}
                    />
                    <span>S</span>
                  </label>
                </td>
              )}
            </tr>
          );
        });
    } else {
      return (
        <tr key={'team_players_empty_row'}>
          <td className="eEventPositions__empty" colSpan={6}>
            {textPlayers}
          </td>
        </tr>
      );
    }
  };

  const picUrl = propz.get(sport, ['field', 'pic']);
  const picUrlExist = typeof picUrl !== 'undefined';
  const isShowSelectTeam = isTeamsExist && !isClubEvent(schoolEvent);

  return (
    <div className="eEventPositions">
      {isShowSelectTeam && renderSelectTeam()}

      <div className={picUrlExist && !isClubEvent(schoolEvent) ? 'eEventPositions__layout' : ''}>
        <div className="eEventPositions__tableWrap">
          <table className="eEventPositions__table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                {isPositionsExist && !isClubEvent(schoolEvent) && <th>Position</th>}
                <th>Form</th>
                {isTeamOrTwoOnTwoSportEvent(schoolEvent) && !isClubEvent(schoolEvent) && <th title="Captain">C</th>}
                {isTeamOrTwoOnTwoSportEvent(schoolEvent) && !isClubEvent(schoolEvent) && (
                  <th title="Vice captain">VC</th>
                )}
                {isTeamOrTwoOnTwoSportEvent(schoolEvent) && !isClubEvent(schoolEvent) && (
                  <th title="Substitute">Sub</th>
                )}
              </tr>
            </thead>
            <tbody>{renderPlayers()}</tbody>
          </table>
        </div>
        {picUrlExist && !isClubEvent(schoolEvent) && (
          <aside className="eEventPositions__fieldPreview">
            <img src={picUrl} alt="Sport field positions" />
          </aside>
        )}
      </div>
    </div>
  );
};
